import { useState } from "react";
import { TrashIcon, Edit3Icon, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { delay, formatDateTimeLocal, formattedDate, formattedDateTime, ToastMessage, useDebounce } from "../../../Components/Utils";
import { Button } from "../../../Components/Buttons";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import payloadHelper from "../../../stores/api/helpers/payload.helper";
import { useBulkDeleteLabReportsMutation, useBulkDestroyLabReportsMutation, useBulkRestoreLabReportsMutation, useDeleteLabReportMutation, useDestroyLabReportMutation, useFetchLabReportsQuery, useFetchTrashedLabReportsQuery, useRestoreLabReportMutation } from "../../../stores/api/lab-report.api";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const LabReportList = () => {
  const navigate = useNavigate();
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const debouncedSearch  = useDebounce(search,500)
  const queryParams = payloadHelper.forList({
    page,
    limit,
    search: debouncedSearch,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchLabReportsQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedLabReportsQuery(queryParams, { skip: !trashed });
  const { data: lab_reports = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [deleteLabReport] = useDeleteLabReportMutation();
  const [destroyLabReport] = useDestroyLabReportMutation();
  const [restoreLabReport] = useRestoreLabReportMutation();
  const [bulkdeleteLabReports] = useBulkDeleteLabReportsMutation();
  const [bulkdestroyLabReports] = useBulkDestroyLabReportsMutation();
  const [bulkrestoreLabReports] = useBulkRestoreLabReportsMutation();

  const resetPage = () => setPage(1);

  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} this Lab Report?`,
      title: `Lab Report ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") {
            await restoreLabReport(row.id).unwrap();
          } else if (type === "destroy") {
            await destroyLabReport(row.id).unwrap();
          } else {
            await deleteLabReport(row.id).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `Lab Report ${type === "restore" ? "Restored" : "Deleted"}!`,
            message: `${type === "restore" ? "Restored" : "Deleted"} Lab Report for patient: ${row?.patient?.patient_name}`,
          });
        } catch (err) {
          handleError(err, "Failed to delete lab report.");
        }
      },
    });
  };

  const handleMultiple = (selectedItems, type = "delete") => {
    const verb = type === "restore" ? "restore" : "delete";
    ToastMessage({
      type: "warning",
      message: `You sure you wanna ${verb} these Lab Report?`,
      title: `Lab Report ${verb === "delete" ? "Delete" : "Restore"}..!`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(selectedItems);
          if (type === "restore") {
            await bulkrestoreLabReports(body).unwrap();
          } else if (type === "destroy") {
            await bulkdestroyLabReports(body).unwrap();
          } else {
            await bulkdeleteLabReports(body).unwrap();
          }
          ToastMessage({
            type: "success",
            title: `${type === "restore" ? "Restored" : "Deleted"} selected Lab Report`,
            message: `${selectedItems.length} Lab Reports ${type === "restore" ? "restored" : "deleted"} successfully.`,
          });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  const headers = [
    { key: "patient.system_number", label: "Patient ID", sortable: true , render: (row) => (
      <div className="text-xs whitespace-nowrap">
        {row?.patient?.system_number ?? row?.patient?.patient_cnic}
      </div>)
    },
    {
      key: "patient.patient_name",
      label: "Patient Details",
      sortable: true,
      render: (row) => (
        <div className="text-xs whitespace-nowrap">
          <div>
            <strong>Name:</strong> {row?.patient?.patient_name}
          </div>
          <div div className="mt-1">
            <strong>F/H:</strong> {row?.patient?.father_husband_name}
          </div>
          <div className="mt-1">
            <span className="bg-green-100 uppercase font-bold  text-end text-green-700 px-2 mt-3 rounded-full text-[10px]">
              {row?.patient?.patient_type}
            </span>
          </div>
        </div>
      ),
    },
    { key: "report_type.name", label: "Report Type", sortable: true },
    {
      key: "status",
      label: "Status",
      type: "status",
    },{ key: "visit_date", label: "Generated at", sortable: true , render: (row) => (
      <div className="text-xs whitespace-nowrap">
        {formattedDateTime(row.createdAt)}
      </div>)
    },
  ];
  
  const renderActions = (row) => (
    <div className="flex gap-2">
      {!trashed ? (
        <Button
          onClick={() => navigate(ROUTE_PATHS.LAB_REPORT_CREATE, { state: { lab_report: row } })}
          title="Edit"
          icon={Edit3Icon}
          className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
        />
      ) : (
        <Button
          onClick={() => handleSingle(row, "restore")}
          title="Restore"
          icon={RotateCcw}
          className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
        />
      )}
      <Button
        onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
        title="Delete"
        icon={TrashIcon}
        className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
      />
    </div>
  );

  return (
    <>
      <BreadCrumb title="Lab Reports" />
      <TableListing
        headers={headers}
        paginationData={lab_reports}
        loading={isFetching}
        createButton
        createLink={ROUTE_PATHS.LAB_REPORT_CREATE}
        actions={renderActions}
        handleBulk={handleMultiple}
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        statusModule={"lab_report"}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
      />
    </>
  );
};

export default LabReportList;
